import Breadcrumb from 'components/Layout/ApplicationLayout/Breadcrumb';
import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import LayoutDefault from 'components/Layout/LayoutDefault';
import { FormTitle } from 'page/Application/ManageCompanyList/component/Form';
import useModalConfirm from 'hook/useModalConfirm';
import { Alert, Form, Spin } from 'antd';
import useCustomMutateRenderMessage from 'hook/useQuery/useCustomMutateRenderMessage';
import useErrorCodePrivacyPolicy from 'page/Application/PrivacyPolicy/hook/useErrorCodePrivacyPolicy';
import { FormLabelGrid } from 'components/Form';
import CKEditorBuilder from 'components/CKEditorBuilder';
import {
  Container,
  FooterButtonContainer,
  FormWrapper,
} from 'components/Form/Template/styled';
import {
  Grid1,
  Grid2,
  GridForm,
} from 'page/Application/ManageNotificationList/component/Form/styled';
import FormFooterSubmit from 'page/Application/ManageProductAndService/Product/component/FormFooterSubmit';
import useErrorNetworkMessage from 'hook/useErrorNetworkMessage';
import useCustomQuery from 'hook/useQuery/useCustomQuery';
import { getPrivacyPolicy, updatePrivacyPolicy } from 'api/privacy/privacy.api';
import { MANAGE_COMPANY_LIST_PATH } from 'constant/appPath';
import useSetTitlePage from 'hook/useSetTitlePage';
import FormMessage from 'components/Form/FormMessage';
import FormNote from 'page/Application/ManageProductAndService/Product/component/FormNote';

const PrivacyPolicy = () => {
  const listItem = useMemo(() => {
    return [
      {
        title: <Link to={'/manage-company-list'}>企業管理</Link>,
      },
      {
        title: '個人情報の取り扱い',
      },
    ];
  }, []);

  const [form] = Form.useForm();
  const { useQueryWithoutParams } = useCustomQuery();
  const navigate = useNavigate();
  const { ERROR_CODE_POLICY_POLICY } = useErrorCodePrivacyPolicy();
  const { handleConfirmAction, contextHolder } = useModalConfirm();
  const { errorMessage, useMutationAPIHandleErrorCode } =
    useCustomMutateRenderMessage({
      errorCode: ERROR_CODE_POLICY_POLICY,
    });
  const { isOnline, errorNetWork, contextErrorNetWork } =
    useErrorNetworkMessage();
  const [content, setContent] = useState(null);
  const [isCancel, setIsCancel] = useState(false);

  const isError = false;

  const { data: privacyData = [], isLoading } = useQueryWithoutParams({
    api: getPrivacyPolicy,
    key: 'privacyPolicy',
    options: {
      refetchOnWindowFocus: false, // cancel fetch data when out focus
    },
  });

  const { mutate: updatePrivacy, isLoading: isLoadingUpdate } =
    useMutationAPIHandleErrorCode({
      api: updatePrivacyPolicy,
      refetchKey: 'updatePrivacyPolicy',
      path: ``,
      messageSuccess: '条項の内容を変更しました。',
    });

  const onFinish = () => {
    if (isOnline) {
      const payload = {
        content: content,
      };
      updatePrivacy(payload);
    } else {
      errorNetWork();
    }
  };

  const handleCancel = () => {
    handleConfirmAction({
      confirmFn: navigate,
      params: `/${MANAGE_COMPANY_LIST_PATH}`,
      title: 'キャンセルしてもよろしいでしょうか？',
      okText: 'はい',
      cancelText: 'キャンセル',
      setIsDisable: setIsCancel,
    });
    setIsCancel(true);
  };

  useEffect(() => {
    setContent(privacyData?.data?.content);
  }, [form, privacyData?.data]);

  useSetTitlePage(`個人情報の取り扱い | Soliton パートナーサイト`, true);

  return (
    <>
      {contextHolder}
      {contextErrorNetWork}
      <Breadcrumb listItem={listItem} titlePage="個人情報の取り扱い" />
      <LayoutDefault>
        <Suspense fallback={isLoading}>
          {contextHolder}
          <FormTitle
            notice={'注意：'}
            title={
              '変更した内容は画面の下部に「保存」ボタンを押下した場合のみに、保存される。'
            }
            type={'text'}
            styles={{ color: '#9D1C49' }}
          />
          <FormWrapper>
            <Container>
              <Spin spinning={isLoadingUpdate}>
                <Form
                  name="privacy-policy"
                  form={form}
                  layout="horizontal"
                  size="large"
                  onFinish={onFinish}
                >
                  <GridForm>
                    <Grid2>
                      {(isError || errorMessage) && (
                        <Alert
                          message={
                            errorMessage ? errorMessage : 'ERROR MESSAGE'
                          }
                          type="error"
                          style={{ marginBottom: '24px' }}
                        />
                      )}
                    </Grid2>
                    {/* start content */}
                    <Grid1>
                      <FormLabelGrid
                        labelText={'条項の内容編集'}
                        required={true}
                      />
                    </Grid1>
                    <Grid2>
                      <Form.Item
                        name={'privacy'}
                        rules={[
                          {
                            validator: () => {
                              if (
                                content === '<p>&nbsp;</p>' ||
                                content === undefined ||
                                content === null
                              ) {
                                return Promise.reject(
                                  <FormMessage
                                    text1={'情報を入力してください。'}
                                    marginBottom={'12px'}
                                  />,
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <CKEditorBuilder
                          data={content}
                          setData={setContent}
                          heightInside={'380px'}
                        />
                        <FormNote
                          text={
                            'ソースコードモードで編集した内容は、ソースコードモードをOFFにしないと反映しません。'
                          }
                        />
                      </Form.Item>
                    </Grid2>
                    {/* end content */}
                    <Grid2>
                      <FooterButtonContainer>
                        <FormFooterSubmit
                          isDisable={isLoading || isLoadingUpdate}
                          onCancel={handleCancel}
                          textOk={'保存'}
                          textCancel={'キャンセル'}
                          isDelete={false}
                          isCancel={isCancel}
                        />
                      </FooterButtonContainer>
                    </Grid2>
                  </GridForm>
                </Form>
              </Spin>
            </Container>
          </FormWrapper>
        </Suspense>
      </LayoutDefault>
    </>
  );
};

export default PrivacyPolicy;
